// src/store/slices/roleSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { roleService, RoleFormData } from '../../services/roleService';
import { authService, Permission, Role } from '../../services/authService';

interface RoleState {
  roles: Role[];
  permissions: Permission[];
  isLoading: boolean;
  error: string | null;
}

const initialState: RoleState = {
  roles: [],
  permissions: [], 
  isLoading: false,
  error: null,
};

// Fetch all permissions
export const fetchAllPermissions = createAsyncThunk(
  'roles/fetchAllPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const permissions = await authService.getAllPermissions();
      return permissions;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch permissions');
    }
  }
);

// Fetch all roles
export const fetchAllRoles = createAsyncThunk(
  'roles/fetchAllRoles',
  async (_, { rejectWithValue }) => {
    try {
      const roles = await roleService.getAllRoles();
      return roles;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch roles');
    }
  }
);

// Create a new role
export const createRole = createAsyncThunk(
  'roles/createRole',
  async (roleData: RoleFormData, { rejectWithValue }) => {
    try {
      const role = await roleService.createRole(roleData);
      return role;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create role');
    }
  }
);

// Update an existing role
export const updateRole = createAsyncThunk(
  'roles/updateRole',
  async ({ id, roleData }: { id: string; roleData: RoleFormData }, { rejectWithValue }) => {
    try {
      const role = await roleService.updateRole(id, roleData);
      return role;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update role');
    }
  }
);

// Delete a role
export const deleteRole = createAsyncThunk(
  'roles/deleteRole',
  async (id: string, { rejectWithValue }) => {
    try {
      await roleService.deleteRole(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete role');
    }
  }
);

const roleSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch permissions cases - ONLY these should set the global error state
      .addCase(fetchAllPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null; // Clear any previous fetch errors
        state.permissions = action.payload;
      })
      .addCase(fetchAllPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })
      // Fetch roles cases - ONLY these should set the global error state
      .addCase(fetchAllRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null; // Clear any previous fetch errors
        state.roles = action.payload;
      })
      .addCase(fetchAllRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string; // Only set error for fetch failures
      })
      // Create role cases - these should NOT set the global error state
      .addCase(createRole.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here - keep fetch errors visible
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles.push(action.payload);
        // Don't modify error state
      })
      .addCase(createRole.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal handle create errors
      })
      // Update role cases - these should NOT set the global error state
      .addCase(updateRole.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.isLoading = false;
        const { id, roleData } = action.meta.arg;
        const index = state.roles.findIndex(role => role._id === id);
        if (index !== -1) {
          state.roles[index] = { ...state.roles[index], ...roleData };
        }
        // Don't modify error state
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal handle update errors
      })
      // Delete role cases - these should NOT set the global error state
      .addCase(deleteRole.pending, (state) => {
        state.isLoading = true;
        // Don't clear the error state here
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = state.roles.filter(role => role._id !== action.payload);
        // Don't modify error state
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isLoading = false;
        // Don't set error state - let the modal/confirmation handle delete errors
      });
  },
});

export const { clearError } = roleSlice.actions;
export default roleSlice.reducer;